import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
// Task 4A — quarantine any legacy demo pointer in chart:active-id before
// the React tree mounts. Idempotent; runs once per browser.
import { migrateTask4aActivePointer } from "./lib/chartSession";
try { migrateTask4aActivePointer(); } catch { /* ignore */ }
// Cross-tab storage defense: any change from another tab (including
// demo visits in tab B that pollute chart:active-id) triggers a
// re-quarantine here. Today listens to the storage event so its
// return-visitor branch reflects the personal pointer immediately.
window.addEventListener("storage", (ev) => {
  if (ev.key === "chart:active-id") {
    try { migrateTask4aActivePointer(); } catch { /* ignore */ }
  }
});
import ErrorBoundary from "./components/ErrorBoundary";
import SkipLink from "./components/SkipLink";
// i18n 2026-08-27 — importing "./i18n" initialises i18next with the
// generated ui.json catalogues (en + hi pilot) and the detect chain
// (path prefix → localStorage → navigator → en). Inline resources make
// init synchronous, so the first paint already renders the detected
// language. Must run before the render call below — ES module imports
// are evaluated first, which guarantees that.
import "./i18n";
import { EntitlementProvider } from "./lib/EntitlementContext";
import PaywallSheet from "./components/ui/PaywallSheet";
import { PersistentAudioProvider } from "./lib/audio/PersistentAudioContext";
import { applyTheme } from "./lib/theme";
import { init as initSentry, captureException } from "./lib/sentry";
import { showError, showWarning } from "./components/ErrorToast";
import "./index.css";
// Self-hosted Google Fonts (Cinzel / Cormorant Garamond / JetBrains Mono / Lora).
// Imported as a side-effect so Vite inlines the @font-face blocks into the main
// CSS bundle. Binaries live in public/fonts/. See public/fonts/README.md.
import "./styles/fonts.css";
// Task 3A — the Benchmark route is lazy-loaded, which means its CSS chunk
// only loads after hydration. On a hard reload of /benchmark that produced
// a flash of unstyled content (the verification at ~1363×936 caught this).
// Loading Benchmark.css eagerly from the main bundle is a ~4KB gzip cost
// shared across all routes — well worth avoiding the FOUC on this page.
// Benchmark.tsx still does `import "./Benchmark.css"` so any consumer of
// the component (including tests) gets the styles automatically.
import "./pages/Benchmark.css";

applyTheme();

// Sentry init (silent no-op when VITE_SENTRY_DSN is unset).
initSentry();

// ── Global error capture ───────────────────────────────────────────────
// React's ErrorBoundary only catches errors thrown during render / lifecycle.
// Async failures (rejected promises in effects, event-handler throws, fetch
// errors) sail past it as unhandled rejections. Capture them here so they are
// at least logged (and surfaced to any future error-reporting service) rather
// than vanishing into the console. See H5 in the Watch redesign spec.
//
// `logGlobalError` does two things: it forwards to Sentry (no PII — just the
// kind tag, never chart payloads / names / ids; see MEMORY §6) AND surfaces
// a visible bottom-right toast so the user isn't left guessing whether
// something failed. The toast auto-dismisses after 8s and is dismissable.
// Messages are intentionally generic; full stack goes to Sentry separately.
function logGlobalError(kind: string, reason: unknown) {
  // PII-safe extra (MEMORY §6): only the kind tag, no payload.
  captureException(reason, { kind });
  // eslint-disable-next-line no-console
  console.error(`[global:${kind}]`, reason);
  // Visible to the user. We pick the message by kind because the raw reason
  // could be an Error object, a string, or anything thrown — never display
  // raw `e.reason` because it can include stack frames with PII.
  if (kind === "unhandledrejection") {
    showError("Something didn't load. Try again in a moment.");
  } else {
    showError("An unexpected error interrupted the page.");
  }
}
window.addEventListener("error", (e) => logGlobalError("error", e.error ?? e.message));
window.addEventListener("unhandledrejection", (e) => logGlobalError("unhandledrejection", e.reason));

// Register the service worker (PWA: installable + offline app shell).
// Production only — in dev the Vite dev server + HMR conflict with the SW's
// asset caching, so we skip it to avoid confusing stale-asset bugs.
// Deferred to the "load" event so it never delays first paint or the
// initial route chunk fetch.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  // Persist the reload guard across navigations within the same session so a
  // broken SKIP_WAITING cycle between two SW versions cannot force an
  // infinite reload loop (each new page load would otherwise reset the guard).
  // See M6 in the Watch redesign spec.
  const RELOAD_KEY = "ag-sw-reloaded";
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (sessionStorage.getItem(RELOAD_KEY)) return;
    sessionStorage.setItem(RELOAD_KEY, "1");
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        reg.update().catch(() => null);
        if (reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
      })
      .catch((err) => console.warn("SW registration failed:", err));
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60, // 1h — readings don't change for a fixed birth time
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/*
      ErrorBoundary sits OUTSIDE everything so any error anywhere in the
      tree gets caught. SkipLink is mounted BEFORE the boundary so keyboard
      users can still reach it even when an error has fired. The boundary
      is intentionally broad — narrow per-route boundaries would isolate
      errors but also let the user navigate away to a broken page; the
      current behavior (show recovery screen, give "Back to home") is
      simpler and more useful.
    */}
    <SkipLink />
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <EntitlementProvider>
            <PersistentAudioProvider>
              <App />
              <PaywallSheet />
            </PersistentAudioProvider>
          </EntitlementProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
